import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { mainAdminApi } from '../config';

interface SubCategory {
  name: string;
  isActive: boolean;
}

interface PillarCategory {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  subCategories?: SubCategory[];
  isActive: boolean;
}

const normalize = (value: string) => value.trim().toLowerCase();

const PillarCategoryPage: React.FC = () => {
  const { pillarSlug } = useParams<{ pillarSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pillars, setPillars] = useState<PillarCategory[]>([]);

  useEffect(() => {
    const fetchPillars = async () => {
      try {
        const response = await mainAdminApi.get<any>('/pillar-categories');
        if (response.data?.categories) {
          setPillars(response.data.categories.filter((p: PillarCategory) => p.isActive));
        }
      } catch (error) {
        console.error('Error fetching pillar categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPillars();
  }, []);

  const selectedPillar = useMemo(() => {
    if (!pillarSlug) return null;
    const decodedSlug = normalize(decodeURIComponent(pillarSlug));
    return pillars.find((pillar) => {
      const nameMatch = normalize(pillar.name) === decodedSlug;
      return nameMatch;
    }) || null;
  }, [pillarSlug, pillars]);

  const activeSubCategories = useMemo(
    () => selectedPillar?.subCategories?.filter((sub) => sub.isActive) || [],
    [selectedPillar]
  );

  return (
    <>
      <style>{`
        :root{
          --ink:#15302B; --ink-soft:#3B5750;
          --paper:#F5F0E3; --paper-2:#ECE4CF; --card:#FFFDF7;
          --marigold:#E6A93F; --marigold-dark:#C98726;
          --forest:#3F6652; --forest-dark:#2C4A3B;
          --line: rgba(21,48,43,0.14); --line-soft: rgba(21,48,43,0.08);
          --shadow: 0 12px 30px -14px rgba(21,48,43,0.28);
        }
        .pc-wrap{max-width:1180px;margin:0 auto;padding:0 32px;}
        @media (max-width:640px){.pc-wrap{padding:0 18px;}}
        .pc-hero{padding:34px 0 18px;border-bottom:1px solid var(--line);}
        .pc-breadcrumb{display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:var(--ink-soft);}
        .pc-breadcrumb a{color:var(--forest-dark);text-decoration:none;}
        .pc-head{display:flex;justify-content:space-between;align-items:flex-end;gap:20px;flex-wrap:wrap;margin-top:16px;}
        .pc-head h1{font-family:'Fraunces',serif;font-size:clamp(30px,4vw,46px);line-height:1.05;margin:0;color:var(--ink);}
        .pc-head p{margin:8px 0 0;color:var(--ink-soft);max-width:720px;font-size:15px;}
        .pc-body{padding:24px 0 72px;}
        .pc-meta{display:flex;align-items:center;gap:10px;margin:18px 0 18px;color:var(--ink-soft);font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:.08em;text-transform:uppercase;}
        .pc-dot{width:4px;height:4px;border-radius:50%;background:var(--line);}
        .pc-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;}
        @media (max-width:1080px){.pc-grid{grid-template-columns:repeat(2,1fr);}}
        @media (max-width:640px){.pc-grid{grid-template-columns:1fr;}}
        .pc-card{background:var(--card);border:1px solid var(--line);border-radius:18px;padding:18px;transition:transform .2s ease, box-shadow .2s ease;cursor:pointer;text-align:left;width:100%;min-height:170px;}
        .pc-card:hover{transform:translateY(-3px);box-shadow:var(--shadow);}
        .pc-card-top{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;}
        .pc-badge{width:34px;height:34px;border-radius:12px;background:linear-gradient(145deg,var(--forest),var(--marigold));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-family:'Fraunces',serif;font-size:16px;}
        .pc-chip{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--ink-soft);}
        .pc-card h3{font-family:'Fraunces',serif;font-size:20px;margin:0 0 7px;color:var(--ink);}
        .pc-card p{margin:0 0 14px;color:var(--ink-soft);font-size:13px;line-height:1.5;}
        .pc-card-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:12.5px;color:var(--forest-dark);font-weight:600;}
        .pc-empty{padding:28px 20px;background:var(--card);border:1px dashed var(--line);border-radius:18px;color:var(--ink-soft);}
      `}</style>

      <div style={{ minHeight: '100vh', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Inter', sans-serif" }}>
        <div className="pc-hero">
          <div className="pc-wrap">
            <div className="pc-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Pillars</span>
              <span>/</span>
              <span>{pillarSlug ? decodeURIComponent(pillarSlug) : 'Explore'}</span>
            </div>

            <div className="pc-head">
              <div>
                <span className="pc-label">View all</span>
                <h1>{loading ? 'Loading pillar...' : (selectedPillar?.name || 'Pillar not found')}</h1>
                <p>{selectedPillar ? `Tap a sub-category to continue.` : 'Pillar not found.'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="pc-body">
          <div className="pc-wrap">
            {selectedPillar ? (
              <>
                <div className="pc-meta">
                  <span>Inside this pillar</span>
                  <span className="pc-dot"></span>
                  <span>{activeSubCategories.length} active sub-categories</span>
                </div>

                {activeSubCategories.length > 0 ? (
                  <div className="pc-grid">
                    {activeSubCategories.map((sub) => (
                      <button
                        key={sub.name}
                        type="button"
                        className="pc-card"
                        onClick={() => navigate(`/speciality/${encodeURIComponent(sub.name.toLowerCase())}`)}
                      >
                        <div className="pc-card-top">
                          <div className="pc-badge">{sub.name.charAt(0).toUpperCase()}</div>
                          <span className="pc-chip">Speciality</span>
                        </div>
                        <h3>{sub.name}</h3>
                        <p>Open {sub.name} practitioners, compare options, and continue to the booking flow.</p>
                        <div className="pc-card-footer">
                          <span>View practitioners</span>
                          <span>→</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="pc-empty">No active sub-categories found inside this pillar.</div>
                )}
              </>
            ) : (
              !loading && (
                <div className="pc-empty">Pillar not found.</div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PillarCategoryPage;
